import React, { useEffect, useState } from "react";
import { Button, DatePicker, Empty, Form, Input, InputNumber, Pagination, Select, Spin, Tag, Upload, Image, message } from "antd";
import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  _create_admin_ambassador_promo,
  _delete_admin_ambassador_promo,
  _get_admin_ambassador_promo_leaderboard,
  _get_admin_ambassador_promos,
  _upload_admin_ambassador_promo_flier,
} from "../../../common/axios_services";
import buildAdminCollapseItem, { AdminCollapseList } from "../../../components/admin/AdminCollapseItem";

const targetOptions = [
  { value: "USER", label: "User" },
  { value: "GROUP_ACCOUNT", label: "Group account" },
  { value: "BUSINESS", label: "Business" },
  { value: "REVENUE", label: "Revenue (NGN)" },
  { value: "STUDENT_AMBASSADOR", label: "Student Ambassador" },
  { value: "AMBASSADOR", label: "Ambassador" },
];



const unwrap = (response) => response?.data?.data || response?.data || [];

const AdminAmbassadorPromos = () => {
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedTargets, setSavedTargets] = useState([]);
  const [flierUrls, setFlierUrls] = useState([]);
  const [tasksPage, setTasksPage] = useState(1);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const selectedLeaderboardTask = tasks.find((task) => task.id === selectedTaskId);

  const handleRemoveFlier = (url) => {
    setFlierUrls((current) => current.filter((item) => item !== url));
    message.info("Promo flier removed.");
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = unwrap(await _get_admin_ambassador_promos());
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to load promo tasks.");
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async (taskId) => {
    try {
      const data = unwrap(await _get_admin_ambassador_promo_leaderboard(taskId));
      setLeaderboard(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to load promo performance.");
    }
  };

  useEffect(() => { loadTasks(); loadLeaderboard(); }, []);

  const handleUpload = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const response = await _upload_admin_ambassador_promo_flier(file);
      const data = unwrap(response);
      const url = data?.url || data?.fileUrl;
      if (!url) throw new Error("Upload did not return a file URL.");
      setFlierUrls((current) => (current.includes(url) ? current : [...current, url]));
      message.success("Promo flier uploaded.");
      onSuccess?.(data);
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || "Promo flier upload failed.");
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveTarget = () => {
    const targetType = form.getFieldValue("targetType");
    const targetValue = form.getFieldValue("targetValue");
    const points = form.getFieldValue("points");

    if (!targetType) {
      message.error("Please select a target type.");
      return;
    }
    if (targetValue === undefined || targetValue === null || String(targetValue).trim() === "") {
      message.error("Please enter a target value.");
      return;
    }
    if (points === undefined || points === null || String(points).trim() === "") {
      message.error("Please enter points for this target.");
      return;
    }

    const target = {
      targetType,
      targetValue: Number(targetValue),
      points: Number(points),
    };
    setSavedTargets((current) => [
      ...current.filter((item) => item.targetType !== target.targetType),
      target,
    ]);
    form.resetFields(["targetType", "targetValue", "points"]);
    form.setFields([
      { name: "targetType", errors: [] },
      { name: "targetValue", errors: [] },
      { name: "points", errors: [] },
    ]);
    const label = targetOptions.find((item) => item.value === target.targetType)?.label || target.targetType;
    message.success(`${label} target saved.`);
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      let targets = [...savedTargets];
      const currentTargetType = form.getFieldValue("targetType");
      const currentTargetValue = form.getFieldValue("targetValue");
      const currentPoints = form.getFieldValue("points");
      if (
        currentTargetType &&
        currentTargetValue !== undefined &&
        currentTargetValue !== null &&
        String(currentTargetValue).trim() !== "" &&
        currentPoints !== undefined &&
        currentPoints !== null &&
        String(currentPoints).trim() !== ""
      ) {
        if (!targets.some((item) => item.targetType === currentTargetType)) {
          targets.push({
            targetType: currentTargetType,
            targetValue: Number(currentTargetValue),
            points: Number(currentPoints),
          });
        }
      }
      if (targets.length === 0) {
        message.error("Save at least 1 promo target before creating the task.");
        return;
      }
      const [startDate, endDate] = values.period;
      await _create_admin_ambassador_promo({
        title: values.title,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
        targetType: targets[0].targetType,
        targetValue: targets[0].targetValue,
        points: targets[0].points,
        promoTargets: targets,
        flierUrl: flierUrls[0] || undefined,
        flierUrls,
      });
      message.success("Ambassador promo task created.");
      form.resetFields();
      setSavedTargets([]);
      setFlierUrls([]);
      await loadTasks();
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to create promo task.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await _delete_admin_ambassador_promo(id);
      message.success("Promo task deleted.");
      if (selectedTaskId === id) setSelectedTaskId(undefined);
      await loadTasks();
      await loadLeaderboard(selectedTaskId === id ? undefined : selectedTaskId);
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to delete promo task.");
    }
  };

  const renderPromoTaskItems = () => {
    const pageSize = 8;
    const start = (tasksPage - 1) * pageSize;
    const paginatedTasks = tasks.slice(start, start + pageSize);

    return paginatedTasks.map((task) => {
      const formattedPeriod = `${dayjs(task.startDate).format("DD MMM YYYY")} - ${dayjs(task.endDate).format("DD MMM YYYY")}`;
      const targets = Array.isArray(task.promoTargets) && task.promoTargets.length
        ? task.promoTargets
        : [{ targetType: task.targetType, targetValue: task.targetValue, points: task.points }];
      const fliers = Array.isArray(task.flierUrls) && task.flierUrls.length
        ? task.flierUrls
        : (task.flierUrl ? [task.flierUrl] : []);

      return buildAdminCollapseItem({
        key: task.id,
        headerTitle: task.title,
        headerTagText: `${Number(task.points).toLocaleString()} Points`,
        headerTagColor: "purple",
        items: [
          { label: "Promo Title", value: task.title },
          { label: "Promo Period", value: formattedPeriod },
          {
            label: "Promo Targets",
            fullWidth: true,
            value: (
              <div className="space-y-1">
                {targets.map((target, index) => (
                  <div key={`${target.targetType}-${index}`}>
                    <strong>{targetOptions.find((opt) => opt.value === target.targetType)?.label || target.targetType}</strong>: {Number(target.targetValue).toLocaleString()} · {Number(target.points).toLocaleString()} points
                  </div>
                ))}
              </div>
            ),
          },
          {
            label: "Promo Fliers",
            value: fliers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {fliers.map((url, index) => <a key={url} href={url} target="_blank" rel="noreferrer" className="text-blue-600 font-gilroyBold hover:underline">View Flier {index + 1}</a>)}
              </div>
            ) : "No fliers attached"
          }
        ],
        actions: (
          <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(task.id)} className="font-gilroyBold">
            Delete Task
          </Button>
        )
      });
    });
  };

  const getPromoPeriodText = (row) => {
    if (selectedLeaderboardTask) {
      return `${dayjs(selectedLeaderboardTask.startDate).format("DD MMM YYYY")} - ${dayjs(selectedLeaderboardTask.endDate).format("DD MMM YYYY")}`;
    }
    const start = row?.startDate || row?.taskStats?.[0]?.startDate || tasks[0]?.startDate;
    const end = row?.endDate || row?.taskStats?.[0]?.endDate || tasks[0]?.endDate;
    if (start && end) {
      return `${dayjs(start).format("DD MMM YYYY")} - ${dayjs(end).format("DD MMM YYYY")}`;
    }
    if (row?.promoPeriod) {
      const parts = row.promoPeriod.split(" - ");
      if (parts.length === 2 && dayjs(parts[0]).isValid() && dayjs(parts[1]).isValid()) {
        return `${dayjs(parts[0]).format("DD MMM YYYY")} - ${dayjs(parts[1]).format("DD MMM YYYY")}`;
      }
      return row.promoPeriod;
    }
    return "All Promo Periods";
  };

  const renderLeaderboardItems = () => {
    const pageSize = 10;
    const start = (leaderboardPage - 1) * pageSize;
    const sortedLeaderboard = [...leaderboard].sort((a, b) => (b.points || 0) - (a.points || 0));
    const paginatedLeaderboard = sortedLeaderboard.slice(start, start + pageSize);

    return paginatedLeaderboard.map((row, index) => {
      const ambassadorName = `${row.firstName || ""} ${row.lastName || ""}`.trim() || row.email || "Unknown Ambassador";
      const rank = start + index + 1;
      const rankBadge = rank === 1 ? "🥇 #1" : rank === 2 ? "🥈 #2" : rank === 3 ? "🥉 #3" : `#${rank}`;
      const postingsCount = selectedLeaderboardTask
        ? Number(row.taskStats?.find((t) => t.taskId === selectedLeaderboardTask.id)?.postingsCount ?? row.postingsCount ?? 0)
        : Number(row.postingsCount ?? row.taskStats?.[0]?.postingsCount ?? 0);
      const periodText = getPromoPeriodText(row);

      return buildAdminCollapseItem({
        key: row.id || index,
        headerTitle: `${rankBadge}  ${ambassadorName}`,
        headerSubtitle: `Promo Period: ${periodText}`,
        headerTags: [
          {
            text: `${Number(row.points || 0).toLocaleString()} Points`,
            color: "purple",
          },
          {
            text: `${postingsCount.toLocaleString()} ${postingsCount === 1 ? "Posting" : "Postings"}`,
            color: "blue",
          },
        ],
        items: [
          { label: "Rank", value: rankBadge },
          { label: "Ambassador Name", value: ambassadorName },
          { label: "Account ID", value: row.accountId || "-" },
          {
            label: "Total Points Earned",
            value: (
              <Tag color="purple" className="font-gilroyBold rounded-md px-2 py-0.5 m-0">
                {Number(row.points || 0).toLocaleString()} Points
              </Tag>
            )
          },
          {
            label: "Number of Postings",
            value: (
              <Tag color="blue" className="font-gilroyBold rounded-md px-2 py-0.5 m-0">
                {postingsCount.toLocaleString()} {postingsCount === 1 ? "Posting" : "Postings"}
              </Tag>
            )
          },
          {
            label: "Promo Period",
            value: (
              <Tag color="cyan" className="font-gilroyBold rounded-md px-2 py-0.5 m-0">
                {periodText}
              </Tag>
            )
          },
          {
            label: "Current Task Progress",
            fullWidth: true,
            value: Array.isArray(row.taskStats) && row.taskStats.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-1 w-full">
                {row.taskStats.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 w-full sm:w-auto text-left">
                    <p className="font-gilroyBold text-gray-900 m-0">{item.title}</p>
                    <p className="text-gray-600 mt-0.5 m-0">
                      Progress: <span className="font-gilroyBold text-green-600">{Number(item.actual || 0).toLocaleString()}</span> / {Number(item.target || 0).toLocaleString()}
                    </p>
                    <p className="text-gray-500 text-xs mt-1 m-0">
                      Postings: <span className="font-gilroyBold text-blue-600">{Number(item.postingsCount || 0).toLocaleString()}</span> · Period: {dayjs(item.startDate).format("DD MMM")} - {dayjs(item.endDate).format("DD MMM YYYY")}
                    </p>
                  </div>
                ))}
              </div>
            ) : "-"
          }
        ]
      });
    });
  };

  return (
    <div className="space-y-6 pb-40">
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <h2 className="font-gilroyBold text-2xl text-gray-900 mb-1">Create Promo Task</h2>
        <p className="text-lg text-gray-600 mb-5">Set a dated ambassador target and the points it is worth.</p>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="title" label="Promo title" rules={[{ required: true, message: "Enter a promo title" }]}>
              <Input size="large" placeholder="e.g. September Growth Promo" />
            </Form.Item>
            <Form.Item name="period" label="Promo period" rules={[{ required: true, message: "Select the promo dates" }]}>
              <DatePicker.RangePicker size="large" className="w-full" disabledDate={(date) => date && date.isBefore(dayjs().startOf("day"))} />
            </Form.Item>
          </div>
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-gilroyBold text-lg text-gray-900 m-0">Promo target</h3>
                <p className="text-gray-500 text-sm m-0">Save a separate target for each target type.</p>
              </div>
              <Button type="primary" onClick={handleSaveTarget} className="font-gilroyBold">
                Save target
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Form.Item name="targetType" label="Target" className="mb-0">
                <Select size="large" options={targetOptions} placeholder="Select target" />
              </Form.Item>
              <Form.Item name="targetValue" label="Target value" className="mb-0">
                <InputNumber size="large" min={0} className="w-full" placeholder="Count or NGN" />
              </Form.Item>
              <Form.Item name="points" label="Points per target" className="mb-0">
                <InputNumber size="large" min={0} className="w-full" placeholder="Points" />
              </Form.Item>
            </div>
            {savedTargets.length > 0 && (
              <div className="border-t border-gray-200 pt-3 space-y-2">
                <h4 className="font-gilroyBold text-gray-900 m-0">Saved promo targets</h4>
                {savedTargets.map((target) => (
                  <div key={target.targetType} className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <span className="font-gilroyBold text-gray-800">{targetOptions.find((item) => item.value === target.targetType)?.label || target.targetType}</span>
                    <span className="text-gray-600">Target: {Number(target.targetValue).toLocaleString()} · {Number(target.points).toLocaleString()} points</span>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setSavedTargets((current) => current.filter((item) => item.targetType !== target.targetType))} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-gilroyBold text-lg text-gray-900 m-0">Promo fliers</h3>
                <p className="text-gray-500 text-sm m-0">Upload one or more fliers for this promo.</p>
              </div>
              <Upload accept="image/*" multiple customRequest={handleUpload} showUploadList={false}>
                <Button icon={<UploadOutlined />} loading={uploading}>Upload flier</Button>
              </Upload>
            </div>
            {flierUrls.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {flierUrls.map((url) => (
                  <div key={url} className="border border-gray-200 rounded-lg overflow-hidden">
                    <Image src={url} alt="Promo flier" className="w-full h-28 object-cover" />
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveFlier(url)} className="w-full">Remove</Button>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-400 text-sm m-0">No promo flier uploaded yet.</p>}
          </div>
          <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={saving} size="large" className="font-gilroyBold">Create Promo Task</Button>
        </Form>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-gilroyBold text-2xl text-gray-900 mb-1">Promo Tasks</h2>
            <p className="text-lg text-gray-600">Tasks are stored in the database and scored live during their date range.</p>
          </div>
          <Select allowClear placeholder="Filter performance by task" value={selectedTaskId} onChange={(value) => { setSelectedTaskId(value); loadLeaderboard(value); }} options={tasks.map((task) => ({ value: task.id, label: task.title }))} className="min-w-[220px]" />
        </div>
        {loading ? (
          <div className="py-8 flex justify-center">
            <Spin size="large" />
          </div>
        ) : tasks.length === 0 ? (
          <Empty description="No promo tasks yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="space-y-4">
            <AdminCollapseList items={renderPromoTaskItems()} />
            {tasks.length > 8 && (
              <div className="flex justify-center mt-4">
                <Pagination
                  current={tasksPage}
                  pageSize={8}
                  total={tasks.length}
                  onChange={(page) => setTasksPage(page)}
                  showSizeChanger={false}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-gilroyBold text-2xl text-gray-900 mb-1">Top Performing Ambassadors</h2>
            <p className="text-lg text-gray-600 m-0">
              Live ranking by points earned against promo targets, including verified number of postings and promo period.
            </p>
          </div>
          {selectedLeaderboardTask ? (
            <Tag color="blue" className="font-gilroyBold text-sm px-3 py-1 rounded-lg m-0">
              Promo Period: {dayjs(selectedLeaderboardTask.startDate).format("DD MMM YYYY")} - {dayjs(selectedLeaderboardTask.endDate).format("DD MMM YYYY")}
            </Tag>
          ) : tasks[0] ? (
            <Tag color="default" className="font-gilroyBold text-sm px-3 py-1 rounded-lg m-0">
              Active Period: {dayjs(tasks[0].startDate).format("DD MMM YYYY")} - {dayjs(tasks[0].endDate).format("DD MMM YYYY")}
            </Tag>
          ) : null}
        </div>
        {leaderboard.length === 0 ? (
          <Empty description="No ambassador performance yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="space-y-4">
            <AdminCollapseList items={renderLeaderboardItems()} />
            {leaderboard.length > 10 && (
              <div className="flex justify-center mt-4">
                <Pagination
                  current={leaderboardPage}
                  pageSize={10}
                  total={leaderboard.length}
                  onChange={(page) => setLeaderboardPage(page)}
                  showSizeChanger={false}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAmbassadorPromos;
